import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './CompactListingCard.css';

function IconHome() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function IconRocket() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function CompactListingCard({
  listing,
  mode = 'manage',
  isAlreadyBoosted = false,
  daysLeft = 0,
  onEdit,
  onView,
  onDelete,
  onMarkSold,
  onUnmarkSold,
  selected = false,
  disabled = false,
  onToggle,
}) {
  const { t } = useLanguage();
  const [imgError, setImgError] = useState(false);

  const photo = listing.images?.[0] || listing.image || null;
  const title = listing.title || t('myListings.untitled');
  const city = listing.city || '';
  const typeLabel = listing.type || '';
  const price = listing.price || 0;
  const priceLabel = '€' + Number(price).toLocaleString();

  const isSold     = listing.status === 'sold';
  const isArchived = listing.status === 'archived';

  const handleCardClick = () => {
    if (mode !== 'select') return;
    if (disabled) return;
    if (typeof onToggle === 'function') onToggle(listing.id);
  };

  const stop = (fn) => (e) => { e.stopPropagation(); if (typeof fn === 'function') fn(); };

  const rootClass = [
    'compact-card',
    `compact-card--${mode}`,
    selected ? 'compact-card--selected' : '',
    disabled ? 'compact-card--disabled' : '',
    isAlreadyBoosted ? 'compact-card--boosted' : '',
    isSold ? 'compact-card--sold' : '',
    isArchived ? 'compact-card--archived' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      onClick={handleCardClick}
      role={mode === 'select' ? 'button' : undefined}
      tabIndex={mode === 'select' && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (mode === 'select' && !disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          if (typeof onToggle === 'function') onToggle(listing.id);
        }
      }}
    >
      <div className="compact-card__thumb">
        {photo && !imgError ? (
          <img
            src={photo}
            alt={title}
            className="compact-card__img"
            loading="lazy"
            decoding="async"
            width="400"
            height="240"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="compact-card__placeholder"><IconHome /></div>
        )}

        {isAlreadyBoosted && (
          <div className="compact-card__boost-badge">
            <IconRocket />
            <span>{t('myListings.boosted')}</span>
          </div>
        )}

        {isSold && (
          <div className="compact-card__sold-overlay" aria-label="Sold">
            <span>{t('myListings.statusSold')}</span>
          </div>
        )}
        {isArchived && (
          <div className="compact-card__sold-overlay compact-card__sold-overlay--archived" aria-label="Archived">
            <span>{t('myListings.statusArchived')}</span>
          </div>
        )}

        {mode === 'select' && (
          <div className="compact-card__select-mark" aria-hidden="true">
            {selected ? <IconCheck /> : <span className="compact-card__select-empty" />}
          </div>
        )}
      </div>

      <div className="compact-card__body">
        <div className="compact-card__title" title={title}>{title}</div>
        <div className="compact-card__meta">
          {city && <span>{city}</span>}
          {city && typeLabel && <span className="compact-card__dot">·</span>}
          {typeLabel && <span className="compact-card__type">{typeLabel}</span>}
          <span className="compact-card__dot">·</span>
          <span className="compact-card__price">{priceLabel}</span>
        </div>

        {isAlreadyBoosted && (
          <div className="compact-card__days-left">
            {t('myListings.daysLeft').replace('{n}', daysLeft)}
          </div>
        )}

        {mode === 'manage' && (
          <div className="compact-card__actions">
            <button type="button" className="compact-card__btn compact-card__btn--ghost" onClick={stop(onView)}>
              {t('myListings.view')}
            </button>
            <button type="button" className="compact-card__btn compact-card__btn--ghost" onClick={stop(onEdit)}>
              {t('myListings.edit')}
            </button>
            {isSold || isArchived ? (
              <button type="button" className="compact-card__btn compact-card__btn--ghost" onClick={stop(onUnmarkSold)}>
                {t('myListings.restore')}
              </button>
            ) : (
              <button type="button" className="compact-card__btn compact-card__btn--ghost" onClick={stop(onMarkSold)}>
                {t('myListings.markSold')}
              </button>
            )}
            <button type="button" className="compact-card__btn compact-card__btn--danger" onClick={stop(onDelete)}>
              {t('myListings.delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
